import React from 'react';
import {Helmet} from 'react-helmet';
import getTagsFromModel from 'epic-node-cms/src/cmsUtils/metaUtils';
import {getLocale} from '@epic-core/common';
import {MessageContext} from '@epic-core/config';
import {LocalizationContext} from '@epic-mw/localization';
import parse from 'url-parse';
import root from 'window-or-global';
import {PageData} from '../types';

const defaultImg = 'https://cdn2.unrealengine.com/ue-logo-1400x788-1400x788-8f185e1e3635.jpg';

export type getMessageFnType = (code: string, args?: any) => string;
interface MetaTags {
    name?: string;
    content: string;
    property?: string;
}

let suffix = ' - Unreal Engine';

export const setSuffix = (newSuffix: string): void => {
    suffix = newSuffix;
};

export const getBasicMetaTags = (
    getMessage?: getMessageFnType,
    messages?: MessageContext
): MetaTags[] => {
    const m = messages || {};
    return [
        {
            name: 'twitter:site',
            content: getMessage
                ? getMessage('epic.ue.social.meta.twitter_account')
                : m['epic.ue.social.meta.twitter_account']
        },
        {
            property: 'og:site_name',
            content: getMessage ? getMessage('epic.ue.unrealengine') : m['epic.ue.unrealengine']
        },
        {
            name: 'twitter:card',
            content: 'summary_large_image'
        },
        {
            property: 'og:type',
            content: 'article'
        }
    ];
};

// Manipulates the title string passed in to remove "Unreal Engine | " and append " - Unreal Engine"
export const titleAppendUnrealEngine = (title: string): string => {
    if (!title) return '';

    //remove prefixed 'Unreal Engine |'
    let updatedTitle = title.replace(/^Unreal Engine \| /i, '');

    //remove - Unreal Engine variations
    updatedTitle = updatedTitle.replace('– Unreal Engine', '');
    updatedTitle = updatedTitle.replace('- Unreal Engine', '');

    //remove encoded ampersands
    updatedTitle = updatedTitle.replace('&amp;', '&');

    //trim trailing spaces
    updatedTitle = updatedTitle.replace(/\s$/, '');

    //apend - Unreal Engine as suffix
    updatedTitle = updatedTitle += suffix;
    return updatedTitle;
};

export const getMetaTags = (
    getMessage: getMessageFnType,
    title: string,
    desc: string,
    img: string
): MetaTags[] => {
    let description = '';
    const fbImage = img;
    const twImage = img;
    const locale = getLocale();
    if (desc) {
        //escape html tags
        description = desc.replace(/(<([^>]+)>)/gi, '');
    } else {
        description = getMessage('epic.ue.social.meta.description');
    }

    const metaLocale = locale ? locale.replace('-', '_') : locale;
    const metaTitle = title || getMessage('epic.ue.social.meta.title');

    const tags: MetaTags[] = [
        // all og tags need to be "property"
        {property: 'og:locale', content: metaLocale},
        {property: 'og:description', content: description},
        {property: 'og:image', content: fbImage},
        {property: 'og:title', content: metaTitle},

        // All twitter meta tags needs to be "name"
        // Description needs to be "name"
        {name: 'description', content: description},
        {name: 'twitter:description', content: description},
        {name: 'twitter:image', content: twImage},
        {name: 'twitter:title', content: metaTitle}
    ];

    const basicTags = getBasicMetaTags(getMessage);
    return tags.concat(basicTags);
};

export const getHelmetForMetaTags = (
    pageModel: PageData,
    publisherName?: string
): React.ReactNode => {
    if (!pageModel) return null;
    const {tags, title} = getTagsFromModel(pageModel);

    publisherName && setSuffix(` - ${publisherName}`);

    let hasImage;
    let hasTitle;
    let hasDescription;
    let hasOpenGraphDescription;
    let hasLocale;
    let hasUrl;

    const currentUrl = parse(root.location.href);
    currentUrl.set('hash', '');
    currentUrl.set('query', '');

    tags.forEach((tag) => {
        if (tag && tag.name === 'description') {
            hasDescription = true;
        } else if (tag && tag.property === 'og:description') {
            hasOpenGraphDescription = true;
        } else if (tag && tag.property === 'og:title') {
            hasTitle = true;
        } else if (tag && tag.property === 'og:image') {
            hasImage = true;
        } else if (tag && tag.property === 'og:locale') {
            hasLocale = false;
        } else if (tag && tag.property === 'og:url') {
            hasUrl = true;
            tag.content = currentUrl.toString();
        }
    });

    if (!hasLocale) {
        tags.push({
            property: 'og:locale',
            content: getLocale()
        });
    }

    if (!hasImage) {
        tags.push({
            property: 'og:image',
            content: defaultImg
        });
        tags.push({
            name: 'twitter:image',
            content: defaultImg
        });
    }

    const links = [
        {
            rel: 'canonical',
            href: currentUrl.toString()
        }
    ];

    if (!hasUrl) {
        tags.push({
            property: 'og:url',
            content: currentUrl.toString()
        });
    }

    return (
        <LocalizationContext.Consumer>
            {(context) => {
                const meta = tags.concat(getBasicMetaTags(undefined, context.messages));
                const metaTitle = title || context.messages['epic.ue.social.meta.title'];
                const appendedTitle = titleAppendUnrealEngine(metaTitle);

                if (!hasTitle) {
                    meta.push({
                        property: 'og:title',
                        content: metaTitle
                    });
                    meta.push({
                        name: 'twitter:title',
                        content: metaTitle
                    });
                }

                if (!hasDescription) {
                    meta.push({
                        name: 'description',
                        content: context.messages['epic.ue.social.meta.description']
                    });
                    meta.push({
                        name: 'twitter:description',
                        content: context.messages['epic.ue.social.meta.description']
                    });
                }

                if (!hasOpenGraphDescription) {
                    meta.push({
                        property: 'og:description',
                        content: context.messages['epic.ue.social.meta.description']
                    });
                }

                return <Helmet link={links} meta={meta} title={appendedTitle} />;
            }}
        </LocalizationContext.Consumer>
    );
};

interface StructuredArticleDataProps {
    title: string;
    description?: string;
    images?: string[];
    datePublished?: string;
    dateModified?: string;
    author?: string;
    publisherName?: string;
    publisherLogo?: string;
}

export const getHelmetStructuredData = (props: StructuredArticleDataProps): JSX.Element => {
    const {
        title = '',
        description = '',
        datePublished = '',
        dateModified = '',
        author,
        publisherName,
        publisherLogo
    } = props;
    const images = props.images || [];

    const currentUrl = parse(root.location.href);
    currentUrl.set('hash', '');
    currentUrl.set('query', '');

    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': currentUrl.toString()
        },
        headline: title,
        image: images,
        datePublished,
        dateModified,
        publisher: {
            '@type': 'Organization',
            name: publisherName || 'Unreal Engine',
            logo: {
                '@type': 'ImageObject',
                url:
                    publisherLogo ||
                    'https://cdn2.unrealengine.com/Unreal+Engine%2Flogos%2FUnreal_Engine_Black-1125x1280-cfa228c80703d4ffbd1cc05eabd5ed380818da45.png'
            }
        },
        description,
        author: {
            '@type': 'Organization',
            name: publisherName || 'Unreal Engine'
        }
    };

    if (author) {
        structuredData.author = {
            '@type': 'Person',
            name: author
        };
    }

    return (
        <Helmet
            script={[
                {
                    type: 'application/ld+json',
                    innerHTML: JSON.stringify(structuredData)
                }
            ]}
        />
    );
};
